import React from "react";
import { createRoot } from "react-dom/client";
import { TiaGPCWidget } from "./TiaGPCWidget";

/**
 * Wrapper de Web Component para TiaGPCWidget
 * Permite usar el widget como un elemento HTML personalizado en cualquier framework
 */
class TiaGPCWebComponent extends HTMLElement {
  constructor() {
    super();
    this.root = null;
    this.mountPoint = null;
  }

  /**
   * Atributos observados - los cambios en estos atributos disparan attributeChangedCallback
   */
  static get observedAttributes() {
    return [
      "token",
      "theme",
      "position",
      "language",
      "primary-color",
      "width",
      "height",
      "api-url",
      "turnstile-site-key",
      "auto-open",
      "show-branding",
      "mobile-fullscreen",
    ];
  }

  /**
   * Se llama cuando el elemento se agrega al DOM
   */
  connectedCallback() {
    this.mountPoint = document.createElement("div");
    this.mountPoint.className = "tia-gpc-mount";
    this.appendChild(this.mountPoint);

    this.root = createRoot(this.mountPoint);

    this.render();
  }

  /**
   * Se llama cuando el elemento se elimina del DOM
   */
  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  /**
   * Se llama cuando un atributo observado cambia
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.root) {
      this.render();
    }
  }

  /**
   * Obtiene props desde los atributos y los convierte al formato de props de React
   */
  getProps() {
    const props = {};

    const token = this.getAttribute("token");
    if (token) props.token = token;

    const theme = this.getAttribute("theme");
    if (theme) props.theme = theme;

    const position = this.getAttribute("position");
    if (position) props.position = position;

    const language = this.getAttribute("language");
    if (language) props.language = language;

    const primaryColor = this.getAttribute("primary-color");
    if (primaryColor) props.primaryColor = primaryColor;

    const width = this.getAttribute("width");
    if (width) props.width = width;

    const height = this.getAttribute("height");
    if (height) props.height = height;

    const apiUrl = this.getAttribute("api-url");
    if (apiUrl) props.apiUrl = apiUrl;

    const turnstileSiteKey = this.getAttribute("turnstile-site-key");
    if (turnstileSiteKey) props.turnstileSiteKey = turnstileSiteKey;

    const autoOpen = this.getAttribute("auto-open");
    if (autoOpen !== null) props.autoOpen = autoOpen !== "false";

    const showBranding = this.getAttribute("show-branding");
    if (showBranding !== null) props.showBranding = showBranding !== "false";

    const mobileFullscreen = this.getAttribute("mobile-fullscreen");
    if (mobileFullscreen !== null)
      props.mobileFullscreen = mobileFullscreen !== "false";

    props.onReady = () => {
      this.dispatchEvent(
        new CustomEvent("ready", {
          bubbles: true,
          composed: true,
        })
      );
    };

    props.onError = (error) => {
      this.dispatchEvent(
        new CustomEvent("error", {
          bubbles: true,
          composed: true,
          detail: error,
        })
      );
    };

    props.onClose = () => {
      this.dispatchEvent(
        new CustomEvent("close", {
          bubbles: true,
          composed: true,
        })
      );
    };

    return props;
  }

  /**
   * Renderiza el componente React
   */
  render() {
    if (!this.root) return;

    const props = this.getProps();

    this.root.render(React.createElement(TiaGPCWidget, props));
  }

  /**
   * Métodos de API pública
   */
  open() {
    this.dispatchEvent(
      new CustomEvent("open-widget", {
        bubbles: true,
        composed: true,
      })
    );
  }

  close() {
    this.dispatchEvent(
      new CustomEvent("close-widget", {
        bubbles: true,
        composed: true,
      })
    );
  }
}

if (typeof window !== "undefined" && !customElements.get("tia-gpc-widget")) {
  customElements.define("tia-gpc-widget", TiaGPCWebComponent);
}

export { TiaGPCWebComponent };